import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { getRouteMeta, getCanonicalPath, isKnownRoute, NOINDEX_ROUTES, SITE_URL } from "@/seo/routeMeta";

import { posts } from "@/data/blogPosts";
import { caseStudies } from "@/data/caseStudies";
import { caseScenarios } from "@/data/caseScenarios";
import { toIsoArticleDate } from "@/seo/articleDate";

const ORG_REF = { "@id": `${SITE_URL}/#organization` };
const ARTICLES = [...posts, ...caseStudies, ...caseScenarios];

const SECTION_LABELS: Record<string, string> = {
  blog: "Blog", "case-studies": "Case studies", "case-scenarios": "Case scenarios",
  industries: "Industries", "how-we-help": "How we help", products: "Product",
  "roi-calculator": "ROI calculator", services: "Services",
};

function buildBreadcrumbs(pathname: string, title: string) {
  if (pathname === "/") return null;
  const parts = pathname.split("/").filter(Boolean);
  const items = [{ name: "Home", url: `${SITE_URL}/` }];
  parts.forEach((part, i) => {
    const last = i === parts.length - 1;
    const path = "/" + parts.slice(0, i + 1).join("/");
    if (!last && !isKnownRoute(path)) return;
    items.push({ name: last ? title.split(" | ")[0] : SECTION_LABELS[part] ?? part.replace(/-/g, " "), url: `${SITE_URL}${path}` });
  });
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({ "@type": "ListItem", position: i + 1, name: it.name, item: it.url })),
  };
}

function buildJsonLd(pathname: string, title: string, description: string, url: string) {
  if (pathname === "/") return null; // Organization + WebSite live in index.html

  if (pathname.startsWith("/blog/") || pathname.startsWith("/case-studies/") || pathname.startsWith("/case-scenarios/")) {
    const entry = ARTICLES.find((a) => a.slug === pathname);
    const iso = entry ? toIsoArticleDate(entry.date) : undefined;
    const image = entry?.image ? (entry.image.startsWith("http") ? entry.image : `${SITE_URL}${entry.image}`) : undefined;
    return {
      "@context": "https://schema.org",
      "@type": pathname.startsWith("/blog/") ? "BlogPosting" : "Article",
      headline: (entry?.title ?? title).slice(0, 110),
      description,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      url,
      ...(image ? { image: [image] } : {}),
      ...(iso ? { datePublished: iso } : {}),
      ...(entry?.category ? { articleSection: entry.category } : {}),
      author: entry?.author
        ? { "@type": "Person", name: entry.author, worksFor: ORG_REF }
        : { "@type": "Organization", name: "Astreon AI Group", ...ORG_REF },
      publisher: { "@type": "Organization", name: "Astreon AI Group", ...ORG_REF },
      inLanguage: "en-GB",
    };
  }

  if (pathname.startsWith("/industries/")) {
    return {
      "@context": "https://schema.org",
      "@type": "Service",
      name: title,
      description,
      url,
      areaServed: "GB",
      provider: { "@type": "Organization", name: "Astreon AI Group", ...ORG_REF },
      serviceType: "AI consulting and implementation",
    };
  }

  if (pathname === "/clinic-genius-ai") {
    return {
      "@context": "https://schema.org",
      "@type": "Service",
      name: title,
      description,
      url,
      areaServed: "GB",
      provider: { "@type": "Organization", name: "Astreon AI Group", ...ORG_REF },
      serviceType: "AI front desk system for clinics",
    };
  }

  if (pathname === "/product" || pathname.startsWith("/products/")) {
    return {
      "@context": "https://schema.org",
      "@type": "Service",
      name: title,
      description,
      url,
      areaServed: "GB",
      provider: { "@type": "Organization", name: "Astreon AI Group", ...ORG_REF },
      serviceType: "AI software and implementation",
    };
  }

  if (pathname.startsWith("/how-we-help/")) {
    return {
      "@context": "https://schema.org",
      "@type": "Service",
      name: title,
      description,
      url,
      provider: { "@type": "Organization", name: "Astreon AI Group", ...ORG_REF },
      serviceType: "AI consulting",
      areaServed: "GB",
    };
  }

  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    description,
    url,
    isPartOf: { "@id": `${SITE_URL}/#website` },
    inLanguage: "en-GB",
  };
}

export default function RouteSeo() {
  const { pathname } = useLocation();
  const { title, description, keywords } = getRouteMeta(pathname);
  const canonicalPath = getCanonicalPath(pathname);
  const url = `${SITE_URL}${canonicalPath === "/" ? "/" : canonicalPath}`;
  const ogType = canonicalPath.startsWith("/blog/") ? "article" : "website";
  const noIndex = NOINDEX_ROUTES.has(canonicalPath) || !isKnownRoute(pathname);
  const jsonLd = noIndex ? null : buildJsonLd(canonicalPath, title, description, url);
  const breadcrumbs = noIndex ? null : buildBreadcrumbs(canonicalPath, title);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(", ")} />
      )}
      {noIndex ? (
        <meta name="robots" content="noindex, follow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      {!noIndex && <link rel="canonical" href={url} />}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
      {breadcrumbs && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbs)}</script>
      )}
    </Helmet>
  );
}
